import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Persisted UI preferences plus two pieces of transient chrome state
 * (shortcut scope stack, command palette). Persisted under `studio.ui` in
 * localStorage; `index.html` reads the same key before first paint to apply
 * the theme without a flash.
 */

export type Theme = 'system' | 'light' | 'dark'
export type Density = 'comfortable' | 'compact'
export type ShortcutScope = 'global' | 'editor' | 'review' | 'timeline' | 'palette' | 'dialog'

export interface PanelSizes {
  /** Width in px of the annotator's right column. */
  annotatorRight: number
  /** Width in px of the library filter rail. */
  libraryRail: number
  /** Height in px of the expanded queue panel. */
  queuePanel: number
}

export interface UiState {
  theme: Theme
  density: Density
  panels: PanelSizes
  /** Which collapsible panels are open, by id. Persisted. */
  panelsOpen: Record<string, boolean>
  /** Bottom-of-stack first; the last entry is the active scope. Not persisted. */
  scopes: ShortcutScope[]
  paletteOpen: boolean
  queuePanelOpen: boolean

  setTheme(theme: Theme): void
  cycleTheme(): void
  setDensity(density: Density): void
  setPanelSize<K extends keyof PanelSizes>(key: K, px: number): void
  togglePanel(id: string, open?: boolean): void
  pushScope(scope: ShortcutScope): void
  popScope(scope: ShortcutScope): void
  setPaletteOpen(open: boolean): void
  setQueuePanelOpen(open: boolean): void
}

const DEFAULT_PANELS: PanelSizes = { annotatorRight: 320, libraryRail: 240, queuePanel: 320 }

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      density: 'comfortable',
      panels: DEFAULT_PANELS,
      panelsOpen: {},
      scopes: ['global'],
      paletteOpen: false,
      queuePanelOpen: false,

      setTheme: (theme) => set({ theme }),
      cycleTheme: () => {
        const order: Theme[] = ['system', 'light', 'dark']
        const next = order[(order.indexOf(get().theme) + 1) % order.length] ?? 'system'
        set({ theme: next })
      },
      setDensity: (density) => set({ density }),
      setPanelSize: (key, px) => set((s) => ({ panels: { ...s.panels, [key]: Math.max(120, Math.round(px)) } })),
      togglePanel: (id, open) => set((s) => ({ panelsOpen: { ...s.panelsOpen, [id]: open ?? !s.panelsOpen[id] } })),
      pushScope: (scope) => set((s) => ({ scopes: [...s.scopes, scope] })),
      popScope: (scope) =>
        set((s) => {
          const i = s.scopes.lastIndexOf(scope)
          if (i <= 0) return {}
          return { scopes: [...s.scopes.slice(0, i), ...s.scopes.slice(i + 1)] }
        }),
      setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
      setQueuePanelOpen: (queuePanelOpen) => set({ queuePanelOpen }),
    }),
    {
      name: 'studio.ui',
      version: 1,
      partialize: (s) => ({ theme: s.theme, density: s.density, panels: s.panels, panelsOpen: s.panelsOpen }),
    },
  ),
)

/** The theme actually rendered, after resolving `system`. */
export function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme
  if (typeof window === 'undefined' || !window.matchMedia) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Mirror theme + density onto `<html data-theme data-density>` and keep them
 * in sync with the store and the OS preference. Call once at startup;
 * returns a disposer (used by tests and HMR).
 */
export function bindUiToDocument(doc: Document = document): () => void {
  const apply = () => {
    const { theme, density } = useUiStore.getState()
    doc.documentElement.dataset.theme = resolveTheme(theme)
    doc.documentElement.dataset.density = density
  }
  apply()
  const unsub = useUiStore.subscribe(apply)
  const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
  mq?.addEventListener('change', apply)
  return () => {
    unsub()
    mq?.removeEventListener('change', apply)
  }
}
